import { sdk } from "@/lib/medusa/client";

export type CheckoutAttemptStage =
  | "event_information"
  | "customer_information"
  | "delivery"
  | "recap"
  | "transfer_cart"
  | "update_cart"
  | "shipping_options"
  | "shipping_method"
  | "payment"
  | "complete_cart"
  | "order_workflow"
  | "completed";

export type CheckoutFailureCode =
  | "cart_transfer_failed"
  | "cart_update_failed"
  | "shipping_options_failed"
  | "shipping_method_failed"
  | "payment_failed"
  | "cart_completion_failed"
  | "fulfillment_outdated"
  | "order_workflow_failed"
  | "unexpected_error";

type CheckoutAttemptBase = {
  attempt_id: string;
  cart_id: string;
  stage: CheckoutAttemptStage;
};

export type CheckoutAttemptEvent = CheckoutAttemptBase & {
  event: "started" | "progress" | "failed" | "order_received" | "completed";
  failure_code?: CheckoutFailureCode;
  order_id?: string;
};

const FAILURE_CODES_BY_STAGE: Partial<Record<CheckoutAttemptStage, CheckoutFailureCode>> = {
  transfer_cart: "cart_transfer_failed",
  update_cart: "cart_update_failed",
  shipping_options: "shipping_options_failed",
  shipping_method: "shipping_method_failed",
  payment: "payment_failed",
  complete_cart: "cart_completion_failed",
  order_workflow: "order_workflow_failed",
};

const storageKey = (cartId: string) => `festi-checkout-attempt:${cartId}`;

export const failureCodeForStage = (stage: CheckoutAttemptStage): CheckoutFailureCode =>
  FAILURE_CODES_BY_STAGE[stage] ?? "unexpected_error";

export const createFailureEvent = (input: CheckoutAttemptBase, rawError?: unknown): CheckoutAttemptEvent => {
  void rawError;
  return {
    ...input,
    event: "failed",
    failure_code: failureCodeForStage(input.stage),
  };
};

export const getOrCreateCheckoutAttemptId = (cartId: string) => {
  const key = storageKey(cartId);
  const existing = window.sessionStorage.getItem(key);
  if (existing) return existing;

  const attemptId = window.crypto.randomUUID();
  window.sessionStorage.setItem(key, attemptId);
  return attemptId;
};

export const getCheckoutAttemptId = (cartId: string) => window.sessionStorage.getItem(storageKey(cartId));

export const linkCheckoutAttemptToCart = (attemptId: string, cartId: string) => {
  window.sessionStorage.setItem(storageKey(cartId), attemptId);
};

export const clearCheckoutAttempt = (attemptId: string) => {
  for (let index = window.sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = window.sessionStorage.key(index);
    if (key?.startsWith("festi-checkout-attempt:") && window.sessionStorage.getItem(key) === attemptId) {
      window.sessionStorage.removeItem(key);
    }
  }
};

export const reportCheckoutAttempt = async (event: CheckoutAttemptEvent): Promise<boolean> => {
  try {
    await sdk.client.fetch("/store/checkout-attempts", {
      method: "POST",
      body: event,
    });
    return true;
  } catch (error) {
    console.warn("Checkout tracking unavailable", error);
    return false;
  }
};
